import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  InviteDesign,
  InviteDesignPage,
  InviteCampaign,
  InviteCampaignStats,
  InviteRecipient,
  CreateInviteDesignInput,
  UpdateInviteDesignInput,
  CreateInviteCampaignInput,
  UpdateInviteCampaignInput,
  AddCampaignRecipientsInput,
  UpdateCampaignRecipientInput,
  EventMealOption,
  CreateEventMealOptionInput,
  GuestPartyMember,
  CreatePartyMemberInput,
  UpdatePartyMemberInput,
  UpdateEventMealOptionInput,
  RsvpQuestionDef,
  CreateRsvpQuestionInput,
  UpdateRsvpQuestionInput,
  GuestMessage,
  CreateGuestMessageInput,
  UpdateGuestMessageInput,
  CheckInListRow,
  EventAttendanceSummary,
  CampaignRecipientPage,
  CampaignSendProgress,
} from '@peles/shared';
import * as invitesApi from '../api/invites';

export function useInviteDesigns(filters?: { eventId?: string; isTemplate?: boolean }) {
  return useQuery<InviteDesign[]>({
    queryKey: ['invite-designs', filters ?? 'all'],
    queryFn: () => invitesApi.getInviteDesigns(filters),
  });
}

export function useCreateInviteDesign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInviteDesignInput) => invitesApi.createInviteDesign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-designs'] });
    },
  });
}

export function useUpdateInviteDesign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateInviteDesignInput }) =>
      invitesApi.updateInviteDesign(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-designs'] });
    },
  });
}

export function useDeleteInviteDesign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invitesApi.deleteInviteDesign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-designs'] });
    },
  });
}

export function useUploadDesignArtwork() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      invitesApi.uploadDesignArtwork(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-designs'] });
    },
  });
}

export function useUploadDesignPage() {
  const queryClient = useQueryClient();
  return useMutation<InviteDesignPage, Error, { id: string; file: File }>({
    mutationFn: ({ id, file }) => invitesApi.uploadDesignPage(id, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-designs'] });
    },
  });
}

export function useDeleteDesignPage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ designId, pageId }: { designId: string; pageId: string }) =>
      invitesApi.deleteDesignPage(designId, pageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-designs'] });
    },
  });
}

export function useSetCampaignPublicLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, enabled }: { campaignId: string; enabled: boolean }) =>
      invitesApi.setCampaignPublicLink(campaignId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-campaigns'] });
    },
  });
}

export function useInviteCampaigns(eventId?: string) {
  return useQuery<InviteCampaign[]>({
    queryKey: ['invite-campaigns', eventId ?? 'all'],
    queryFn: () => invitesApi.getInviteCampaigns(eventId),
  });
}

export function useCreateInviteCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInviteCampaignInput) => invitesApi.createInviteCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-campaigns'] });
    },
  });
}

export function useUpdateInviteCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateInviteCampaignInput }) =>
      invitesApi.updateInviteCampaign(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-campaigns'] });
    },
  });
}

export function useDeleteInviteCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invitesApi.deleteInviteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-campaigns'] });
    },
  });
}

/** One campaign, refreshed every few seconds while it is going out. */
export function useInviteCampaign(campaignId?: string) {
  return useQuery<InviteCampaign>({
    queryKey: ['invite-campaigns', 'one', campaignId],
    queryFn: () => invitesApi.getInviteCampaign(campaignId!),
    enabled: !!campaignId,
    refetchInterval: (query) => (isGoingOut(query.state.data) ? SEND_POLL_MS : false),
  });
}

/** How often a campaign on its way out is looked at again. */
export const SEND_POLL_MS = 3000;

/** Queued for now or sending — what the page follows with progress. */
export function isGoingOut(campaign?: Pick<InviteCampaign, 'status' | 'scheduledAt'> | null) {
  if (!campaign) return false;
  if (campaign.status === 'SENDING') return true;
  return (
    campaign.status === 'SCHEDULED' &&
    !!campaign.scheduledAt &&
    new Date(campaign.scheduledAt).getTime() <= Date.now()
  );
}

/** How far a send has got, followed while it is going out. */
export function useCampaignProgress(campaignId: string | undefined, active: boolean) {
  return useQuery<CampaignSendProgress>({
    queryKey: ['invite-progress', campaignId],
    queryFn: () => invitesApi.getCampaignProgress(campaignId!),
    enabled: !!campaignId,
    refetchInterval: active ? SEND_POLL_MS : false,
  });
}

/** One page of a campaign's recipients, filtered. */
export function useCampaignRecipientPage(
  campaignId: string | undefined,
  query: invitesApi.CampaignRecipientQuery,
  options: { poll?: boolean } = {},
) {
  return useQuery<CampaignRecipientPage>({
    queryKey: ['invite-recipients', campaignId, 'page', query],
    queryFn: () => invitesApi.getCampaignRecipientPage(campaignId!, query),
    enabled: !!campaignId,
    placeholderData: (previous) => previous,
    refetchInterval: options.poll ? SEND_POLL_MS : false,
  });
}

export function useResendCampaignRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, recipientId }: { campaignId: string; recipientId: string }) =>
      invitesApi.resendCampaignRecipient(campaignId, recipientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-recipients'] });
      queryClient.invalidateQueries({ queryKey: ['invite-stats'] });
    },
  });
}

export function useTestSendInviteCampaign() {
  return useMutation({
    mutationFn: (campaignId: string) => invitesApi.testSendInviteCampaign(campaignId),
  });
}

export function useCampaignRecipients(campaignId?: string, status?: string) {
  return useQuery<InviteRecipient[]>({
    queryKey: ['invite-recipients', campaignId, status ?? 'all'],
    queryFn: () => invitesApi.getCampaignRecipients(campaignId!, status),
    enabled: !!campaignId,
  });
}

export function useAddCampaignRecipients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      input,
    }: {
      campaignId: string;
      input: AddCampaignRecipientsInput;
    }) => invitesApi.addCampaignRecipients(campaignId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-recipients'] });
      queryClient.invalidateQueries({ queryKey: ['invite-campaigns'] });
    },
  });
}

export function useUpdateCampaignRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      recipientId,
      updates,
    }: {
      campaignId: string;
      recipientId: string;
      updates: UpdateCampaignRecipientInput;
    }) => invitesApi.updateCampaignRecipient(campaignId, recipientId, updates),
    onSuccess: () => {
      // The override writes through to the guest row, so the guest list and
      // the campaign's counters both go stale.
      queryClient.invalidateQueries({ queryKey: ['invite-recipients'] });
      queryClient.invalidateQueries({ queryKey: ['invite-stats'] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
  });
}

export function useRemoveCampaignRecipient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, recipientId }: { campaignId: string; recipientId: string }) =>
      invitesApi.removeCampaignRecipient(campaignId, recipientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-recipients'] });
      queryClient.invalidateQueries({ queryKey: ['invite-campaigns'] });
    },
  });
}

export function useSendInviteCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => invitesApi.sendInviteCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-recipients'] });
      queryClient.invalidateQueries({ queryKey: ['invite-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['invite-stats'] });
      queryClient.invalidateQueries({ queryKey: ['invite-progress'] });
    },
  });
}

export function useInviteCampaignStats(campaignId?: string) {
  return useQuery<InviteCampaignStats>({
    queryKey: ['invite-stats', campaignId],
    queryFn: () => invitesApi.getInviteCampaignStats(campaignId!),
    enabled: !!campaignId,
  });
}

// ─── Phase 2: sub-events, meal options, party members, questions ────────────

export function useSubEvents(eventId?: string) {
  return useQuery({
    queryKey: ['invite-sub-events', eventId],
    queryFn: () => invitesApi.getSubEvents(eventId!),
    enabled: !!eventId,
  });
}

export function useMealOptions(eventId?: string) {
  return useQuery<EventMealOption[]>({
    queryKey: ['meal-options', eventId],
    queryFn: () => invitesApi.getMealOptions(eventId!),
    enabled: !!eventId,
  });
}

export function useCreateMealOption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, input }: { eventId: string; input: CreateEventMealOptionInput }) =>
      invitesApi.createMealOption(eventId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-options'] });
    },
  });
}

export function useUpdateMealOption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      optionId,
      updates,
    }: {
      eventId: string;
      optionId: string;
      updates: UpdateEventMealOptionInput;
    }) => invitesApi.updateMealOption(eventId, optionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-options'] });
    },
  });
}

export function useDeleteMealOption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, optionId }: { eventId: string; optionId: string }) =>
      invitesApi.deleteMealOption(eventId, optionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meal-options'] });
    },
  });
}

// ─── Party members (producer-managed couple/family contacts) ────────────────

export function useGuestPartyMembers(guestId?: string) {
  return useQuery<GuestPartyMember[]>({
    queryKey: ['party-members', guestId],
    queryFn: () => invitesApi.getPartyMembers(guestId!),
    enabled: !!guestId,
  });
}

export function useCreatePartyMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ guestId, input }: { guestId: string; input: CreatePartyMemberInput }) =>
      invitesApi.createPartyMember(guestId, input),
    onSuccess: (_data, { guestId }) => {
      queryClient.invalidateQueries({ queryKey: ['party-members', guestId] });
    },
  });
}

export function useUpdatePartyMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      guestId,
      memberId,
      updates,
    }: {
      guestId: string;
      memberId: string;
      updates: UpdatePartyMemberInput;
    }) => invitesApi.updatePartyMember(guestId, memberId, updates),
    onSuccess: (_data, { guestId }) => {
      queryClient.invalidateQueries({ queryKey: ['party-members', guestId] });
    },
  });
}

export function useDeletePartyMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ guestId, memberId }: { guestId: string; memberId: string }) =>
      invitesApi.deletePartyMember(guestId, memberId),
    onSuccess: (_data, { guestId }) => {
      queryClient.invalidateQueries({ queryKey: ['party-members', guestId] });
    },
  });
}

export function useCampaignQuestions(campaignId?: string) {
  return useQuery<RsvpQuestionDef[]>({
    queryKey: ['invite-questions', campaignId],
    queryFn: () => invitesApi.getCampaignQuestions(campaignId!),
    enabled: !!campaignId,
  });
}

export function useAddCampaignQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, input }: { campaignId: string; input: CreateRsvpQuestionInput }) =>
      invitesApi.addCampaignQuestion(campaignId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-questions'] });
    },
  });
}

export function useUpdateCampaignQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      questionId,
      updates,
    }: {
      questionId: string;
      updates: UpdateRsvpQuestionInput;
    }) => invitesApi.updateCampaignQuestion(questionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-questions'] });
    },
  });
}

export function useDeleteCampaignQuestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) => invitesApi.deleteCampaignQuestion(questionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-questions'] });
    },
  });
}

// ─── Phase 3: reminders, scheduling, guest messages ─────────────────────────

export function useRemindInviteCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, recipientIds }: { campaignId: string; recipientIds?: string[] }) =>
      invitesApi.remindInviteCampaign(campaignId, recipientIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-recipients'] });
    },
  });
}

export function useScheduleInviteCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, scheduledAt }: { campaignId: string; scheduledAt: string | null }) =>
      invitesApi.scheduleInviteCampaign(campaignId, scheduledAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-campaigns'] });
    },
  });
}

export function useGuestMessages(eventId?: string) {
  return useQuery<GuestMessage[]>({
    queryKey: ['guest-messages', eventId ?? 'all'],
    queryFn: () => invitesApi.getGuestMessages(eventId),
  });
}

export function useCreateGuestMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateGuestMessageInput) => invitesApi.createGuestMessage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-messages'] });
    },
  });
}

export function useUpdateGuestMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateGuestMessageInput }) =>
      invitesApi.updateGuestMessage(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-messages'] });
    },
  });
}

export function useDeleteGuestMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invitesApi.deleteGuestMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-messages'] });
    },
  });
}

export function useSendGuestMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invitesApi.sendGuestMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-messages'] });
    },
  });
}

// ─── Phase 4: check-in & attendance ─────────────────────────────────────────

export function useEventAttendance(eventId?: string) {
  return useQuery<EventAttendanceSummary>({
    queryKey: ['event-attendance', eventId],
    queryFn: () => invitesApi.getEventAttendance(eventId!),
    enabled: !!eventId,
  });
}

/** Who a check-in is for: a guest from the list, or the code on their invitation. */
export type CheckInTarget = { guestId: string } | { token: string };

export const checkInListKey = (eventId?: string) => ['checkin-list', eventId] as const;

/** The door's list — every party, expected and arrived. */
export function useCheckInList(eventId?: string) {
  return useQuery<CheckInListRow[]>({
    queryKey: checkInListKey(eventId),
    queryFn: () => invitesApi.getCheckInList(eventId!),
    enabled: !!eventId,
  });
}

/**
 * Check a party in, in part or whole, or undo it. The answer is written
 * straight into the door's list, so the row changes before the refetch lands.
 */
export function useCheckIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ target, body }: { target: CheckInTarget; body?: invitesApi.CheckInBody }) =>
      'guestId' in target
        ? invitesApi.checkInGuest(target.guestId, body)
        : invitesApi.checkInByToken(target.token, body),
    onSuccess: (result, { body }) => {
      queryClient.setQueryData<CheckInListRow[]>(checkInListKey(body?.eventId), (rows) =>
        rows?.map((row) =>
          row.guestId === result.guest.guestId ? { ...row, ...result.guest } : row,
        ),
      );
      queryClient.invalidateQueries({ queryKey: ['event-attendance'] });
      queryClient.invalidateQueries({ queryKey: ['checkin-list'] });
      queryClient.invalidateQueries({ queryKey: ['guests'] });
    },
    // A refusal — above all "changed at another door" — means the list on
    // screen is behind the server: fetch it again rather than leave a party
    // shown as it was before the other door's arrival.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['event-attendance'] });
      queryClient.invalidateQueries({ queryKey: ['checkin-list'] });
    },
  });
}
